/** @jsx jsx */
import { jsx } from '@emotion/react';

import { Typography2 } from '@coursera/cds-core';

import ACECreditRecommendationInfo from 'bundles/unified-description-page-common/components/modules/credits/ACECreditRecommendationInfo';
import EligibleDegreesInfo from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesInfo';
import { EligibleDegreesModule } from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesModule';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import getEligibleDegreesDescription from 'bundles/unified-description-page-common/utils/getEligibleDegreesDescription';
import getEligibleDegreesTitle from 'bundles/unified-description-page-common/utils/getEligibleDegreesTitle';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import isForCreditNonDegree from 'bundles/unified-description-page-common/utils/isForCreditNonDegree';

import _t from 'i18n!nls/xdp';

type PageData = ConsumerCoursePageFieldsFragment | ConsumerSpecializationPageFieldsFragment;

type Props = {
  pageData: Pick<PageData, 'degrees' | 'partners' | 'slug'> & {
    productTags?: ConsumerSpecializationPageFieldsFragment['productTags'];
    productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
  };
  config?: {
    disableCta?: boolean;
  };
};

export const getProductVariant = (pageData: {
  primarySpecialization?: ConsumerCoursePageFieldsFragment['primarySpecialization'];
  productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
}) => {
  if (pageData.productVariant) {
    return pageData.productVariant;
  } else if (pageData.primarySpecialization?.productVariant) {
    return pageData.primarySpecialization.productVariant;
  }
  return undefined;
};

const ConsumerEligibleDegreesModuleRenderer = ({ pageData, config }: Props) => {
  const hasDegrees = pageData.degrees && pageData.degrees.length > 0;
  const hasACELink = pageData.productTags?.linkToACECreditRecommendation;

  if (!hasDegrees && !hasACELink) {
    return null;
  }

  const eligibleDegreesTitle = hasDegrees
    ? getEligibleDegreesTitle(pageData.degrees ?? [])
    : _t('Get a head start on your degree');

  const isFCND = isForCreditNonDegree(pageData?.slug, pageData?.partners?.[0]?.shortName || '');
  const variantTitle = getVariantTitle(pageData.productVariant);

  const { description: eligibleDegreesDescription, disclaimer: eligibleDegreesDisclaimer } =
    getEligibleDegreesDescription(
      variantTitle,
      getProductVariant(pageData),
      pageData.degrees?.[0]?.upsellPartners?.[0]?.name,
      pageData.degrees?.[0]?.upsellType,
      isFCND,
      pageData?.partners?.[0]?.name
    );

  return (
    <EligibleDegreesModule
      title={
        <Typography2 variant="titleSmall" component="h2" color="body">
          {eligibleDegreesTitle}
        </Typography2>
      }
    >
      {hasDegrees && (
        <EligibleDegreesInfo
          modalTitle={eligibleDegreesTitle}
          description={eligibleDegreesDescription}
          disclaimer={eligibleDegreesDisclaimer}
          degrees={pageData.degrees}
          disableCta={config?.disableCta}
        />
      )}
      {hasACELink && (
        <ACECreditRecommendationInfo
          variantTitle={variantTitle}
          // @ts-expect-error URL: string
          aceLink={pageData.productTags?.linkToACECreditRecommendation}
          disableCta={config?.disableCta}
        />
      )}
    </EligibleDegreesModule>
  );
};

export default ConsumerEligibleDegreesModuleRenderer;
